import React from 'react';
import { Link } from 'react-router-dom';
import { useHelp } from '../../context/HelpContext';
import PropTypes from 'prop-types';

const HelpLeftNav = ({ isExpanded, currentPath, theme }) => {
  const { 
    staticRoutes,
    fileTree,
    ui: { documentationMode, expandedSections, searchExpanded },
    navigation: { searchQuery, searchResults, isSearching },
    toggleSection,
    toggleDocumentationMode,
    toggleFolder,
    navigateToFile,
    setSearchQuery
  } = useHelp();

  // Iconos SVG
  const icons = {
    home: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9,22 9,12 15,12 15,22"/>
      </svg>
    ),
    ticket: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v2Z"/>
        <path d="M13 5v2"/>
        <path d="M13 17v2"/>
        <path d="M13 11v2"/>
      </svg>
    ),
    documentation: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14,2 14,8 20,8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10,9 9,9 8,9"/>
      </svg>
    ),
    privacy: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
        <circle cx="12" cy="16" r="1"/>
        <path d="m7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    ),
    contact: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
      </svg>
    ),
    chevron: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="9,18 15,12 9,6"/>
      </svg>
    ),
    api: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="16,18 22,12 16,6"/>
        <polyline points="8,6 2,12 8,18"/>
      </svg>
    ),
    database: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <ellipse cx="12" cy="5" rx="9" ry="3"/>
        <path d="m3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/>
        <path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3"/>
      </svg>
    ),
    backend: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect width="20" height="8" x="2" y="2" rx="2" ry="2"/>
        <rect width="20" height="8" x="2" y="14" rx="2" ry="2"/>
        <line x1="6" y1="6" x2="6.01" y2="6"/>
        <line x1="6" y1="18" x2="6.01" y2="18"/>
      </svg>
    ),
    frontend: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect width="14" height="8" x="5" y="2" rx="2"/>
        <rect width="20" height="8" x="2" y="14" rx="2"/>
        <path d="M6 18h2"/>
        <path d="M12 18h6"/>
      </svg>
    ),
    explorer: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-5l-2-2H5a2 2 0 0 0-2 2z"/>
      </svg>
    ),
    folder: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-5l-2-2H5a2 2 0 0 0-2 2z"/>
      </svg>
    ),
    folderOpen: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 4h8l2 2h4a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/>
        <path d="M6 8h12"/>
      </svg>
    ),
    file: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14,2 14,8 20,8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10,9 9,9 8,9"/>
      </svg>
    ),
    search: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8"/>
        <path d="m21 21-4.35-4.35"/>
      </svg>
    ),
    close: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    )
  };

  // Función para toggle de secciones estáticas
  const handleToggleSection = (section) => {
    if (!isExpanded) return;
    toggleSection(section);
  };

  // Función para alternar modo documentación
  const handleToggleDocumentationMode = () => {
    if (!isExpanded) return;
    toggleDocumentationMode();
  };

  // Componente TreeNode para renderizar el explorador de archivos
  const TreeNode = ({ node, level = 0 }) => {
    const isFolder = node.type === 'folder';
    const isExpanded = isFolder ? node.isExpanded : false;
    const isActive = currentPath === node.path;
    const hasChildren = isFolder && node.children && node.children.length > 0;

    const handleClick = (e) => {
      e.stopPropagation();
      
      if (isFolder) {
        toggleFolder(node.id);
      } else {
        navigateToFile(node);
      }
    };

    const handleToggle = (e) => {
      e.stopPropagation();
      if (isFolder) {
        toggleFolder(node.id);
      }
    };

    return (
      <div className={`GM__${theme}-help-nav-explorer-node`}>
        <div 
          className={`GM__${theme}-help-nav-explorer-item ${isActive ? 'active' : ''}`}
          style={{ paddingLeft: `${8 + (level * 16)}px` }}
          onClick={handleClick}
        >
          {/* Chevron para carpetas */}
          {isFolder && (
            <button 
              className={`GM__${theme}-help-nav-explorer-chevron ${isExpanded ? 'expanded' : ''}`}
              onClick={handleToggle}
              disabled={!hasChildren}
            >
              {hasChildren && icons.chevron}
            </button>
          )}
          
          {/* Espaciador para archivos */}
          {!isFolder && <div className={`GM__${theme}-help-nav-explorer-spacer`} />}
          
          {/* Icono */}
          <span className={`GM__${theme}-help-nav-explorer-icon`}>
            {isFolder 
              ? (isExpanded ? icons.folderOpen : icons.folder)
              : icons.file
            }
          </span>
          
          {/* Nombre */}
          <span className={`GM__${theme}-help-nav-explorer-name`}>
            {node.name}
          </span>
        </div>
        
        {/* Renderizar hijos si la carpeta está expandida */}
        {isFolder && isExpanded && hasChildren && (
          <div className={`GM__${theme}-help-nav-explorer-children`}>
            {node.children.map((child) => (
              <TreeNode 
                key={child.id} 
                node={child} 
                level={level + 1} 
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Componente de búsqueda compacta
  const CompactSearch = () => (
    <div className={`GM__${theme}-help-nav-search`}>
      <div className={`GM__${theme}-help-nav-search-input-wrapper`}>
        <span className={`GM__${theme}-help-nav-search-icon`}>
          {isSearching ? (
            <div className={`GM__${theme}-help-nav-search-spinner`} />
          ) : (
            icons.search
          )}
        </span>
        
        <input
          type="text"
          className={`GM__${theme}-help-nav-search-input`}
          placeholder="Buscar..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        
        {searchQuery && (
          <button
            className={`GM__${theme}-help-nav-search-clear`}
            onClick={() => setSearchQuery('')}
          >
            {icons.close}
          </button>
        )}
      </div>
      
      {/* Resultados de búsqueda */}
      {searchQuery && searchResults.length > 0 && (
        <div className={`GM__${theme}-help-nav-search-results`}>
          {searchResults.map((result, index) => (
            <div
              key={`${result.id}-${index}`}
              className={`GM__${theme}-help-nav-search-result`}
              onClick={() => navigateToFile(result)}
            >
              <span className={`GM__${theme}-help-nav-search-result-icon`}>
                {result.type === 'folder' ? icons.folder : icons.file}
              </span>
              <span className={`GM__${theme}-help-nav-search-result-name`}>
                {result.name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Renderizar rutas estáticas
  const renderStaticRoutes = () => (
    <>
      {/* Rutas principales */}
      <div className={`GM__${theme}-help-nav-section`}>
        <ul className={`GM__${theme}-help-nav-items`}>
          {staticRoutes.main.map((item) => (
            <li key={item.key} className={`GM__${theme}-help-nav-item`}>
              <Link
                to={item.route.path}
                className={`GM__${theme}-help-nav-link ${
                  currentPath === item.route.path ? 'active' : ''
                }`}
              >
                <span className={`GM__${theme}-help-nav-icon`}>
                  {icons[item.icon]}
                </span>
                <span className={`GM__${theme}-help-nav-text`}>
                  {item.route.title}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Documentación */}
      <div className={`GM__${theme}-help-nav-section`}>
        <div className={`GM__${theme}-help-nav-title`}>
          {staticRoutes.documentation.title}
        </div>
        
        <ul className={`GM__${theme}-help-nav-items`}>
          <li className={`GM__${theme}-help-nav-item has-submenu`}>
            <button
              className={`GM__${theme}-help-nav-link`}
              onClick={() => handleToggleSection('documentation')}
              disabled={!isExpanded}
            >
              <span className={`GM__${theme}-help-nav-icon`}>
                {icons[staticRoutes.documentation.icon]}
              </span>
              <span className={`GM__${theme}-help-nav-text`}>
                {staticRoutes.documentation.title}
              </span>
              <span className={`GM__${theme}-help-nav-chevron ${
                expandedSections.documentation ? 'expanded' : ''
              }`}>
                {icons.chevron}
              </span>
            </button>
            
            <ul className={`GM__${theme}-help-nav-submenu ${
              !isExpanded ? 'collapsed-nav' : (expandedSections.documentation ? 'expanded-submenu' : '')
            }`}>
              {staticRoutes.documentation.items.map((item) => (
                <li key={item.key} className={`GM__${theme}-help-nav-subitem`}>
                  <Link
                    to={item.route.path}
                    className={`GM__${theme}-help-nav-sublink ${
                      currentPath === item.route.path ? 'active' : ''
                    }`}
                  >
                    {item.route.title}
                  </Link>
                </li>
              ))}
              
              {/* Botón para acceder al explorador de archivos */}
              <li className={`GM__${theme}-help-nav-subitem`}>
                <button
                  className={`GM__${theme}-help-nav-sublink ${
                    documentationMode ? 'active' : ''
                  }`}
                  onClick={handleToggleDocumentationMode}
                >
                  <span className={`GM__${theme}-help-nav-icon`}>
                    {icons.explorer}
                  </span>
                  Explorador de Archivos
                </button>
              </li>
            </ul>
          </li>
        </ul>
      </div>

      {/* Privacidad y datos */}
      <div className={`GM__${theme}-help-nav-section`}>
        <div className={`GM__${theme}-help-nav-title`}>
          {staticRoutes.privacy.title}
        </div>
        
        <ul className={`GM__${theme}-help-nav-items`}>
          <li className={`GM__${theme}-help-nav-item has-submenu`}>
            <button
              className={`GM__${theme}-help-nav-link`}
              onClick={() => handleToggleSection('privacy')}
              disabled={!isExpanded}
            >
              <span className={`GM__${theme}-help-nav-icon`}>
                {icons[staticRoutes.privacy.icon]}
              </span>
              <span className={`GM__${theme}-help-nav-text`}>
                {staticRoutes.privacy.title}
              </span>
              <span className={`GM__${theme}-help-nav-chevron ${
                expandedSections.privacy ? 'expanded' : ''
              }`}>
                {icons.chevron}
              </span>
            </button>
            
            <ul className={`GM__${theme}-help-nav-submenu ${
              !isExpanded ? 'collapsed-nav' : (expandedSections.privacy ? 'expanded-submenu' : '')
            }`}>
              {staticRoutes.privacy.items.map((item) => (
                <li key={item.key} className={`GM__${theme}-help-nav-subitem`}>
                  <Link
                    to={item.route.path}
                    className={`GM__${theme}-help-nav-sublink ${
                      currentPath === item.route.path ? 'active' : ''
                    }`}
                  >
                    {item.route.title}
                  </Link>
                </li>
              ))}
            </ul>
          </li>
        </ul>
      </div>

      {/* Contacto */}
      <div className={`GM__${theme}-help-nav-section`}>
        <ul className={`GM__${theme}-help-nav-items`}>
          {staticRoutes.contact.map((item) => (
            <li key={item.key} className={`GM__${theme}-help-nav-item`}>
              <Link
                to={item.route.path}
                className={`GM__${theme}-help-nav-link ${
                  currentPath === item.route.path ? 'active' : ''
                }`}
              >
                <span className={`GM__${theme}-help-nav-icon`}>
                  {icons[item.icon]}
                </span>
                <span className={`GM__${theme}-help-nav-text`}>
                  {item.route.title}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </>
  );

  // Renderizar explorador de archivos
  const renderFileExplorer = () => (
    <div className={`GM__${theme}-help-nav-section`}>
      <div className={`GM__${theme}-help-nav-title`}>
        Explorador de Archivos
        <button
          className={`GM__${theme}-help-nav-back-button`}
          onClick={handleToggleDocumentationMode}
        >
          ← Volver
        </button>
      </div>
      
      {/* Búsqueda compacta */}
      <CompactSearch />
      
      {/* Árbol de archivos */}
      <div className={`GM__${theme}-help-nav-explorer`}>
        <TreeNode node={fileTree} />
      </div>
    </div>
  );

  return (
    <nav className={`GM__${theme}-help-nav ${isExpanded ? '' : 'collapsed'}`}>
      {documentationMode ? renderFileExplorer() : renderStaticRoutes()}
    </nav>
  );
};

HelpLeftNav.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  currentPath: PropTypes.string.isRequired,
  theme: PropTypes.string.isRequired
};

export default HelpLeftNav;